
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  HiOutlineVideoCamera,
  HiOutlinePlay,
  HiOutlineArrowDown,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineRectangleStack,
} from "react-icons/hi2";

import { Header, Fotter } from "../components/layout";
import { Loader, VideoCategoryCard } from "../components/utils";

import { getAllVideoCategorys } from "../redux/actions/videoCategory";
import { VideoCategoryImage } from "../assets";

const PAGE_SIZE = 9;

/* ============================================
   PAGINATION HELPER
============================================ */

const getVisiblePages = (current, total) => {
  const count = Math.min(total, 5);

  const start = Math.max(
    0,
    Math.min(current - 2, total - count)
  );

  return Array.from(
    { length: count },
    (_, index) => start + index
  );
};

/* ============================================
   VIDEO CATEGORIES PAGE
============================================ */

function VideoCategoryPage() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const [page, setPage] = useState(0);

  const {
    isLoading,
    videoCategorys,
    pagination,
  } = useSelector(
    (state) => state.VideoCategory || {}
  );

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  /* ======================================
     FETCH CATEGORIES
  ====================================== */

  useEffect(() => {
    dispatch(getAllVideoCategorys(page, PAGE_SIZE));
  }, [dispatch, page]);

  const categories = Array.isArray(videoCategorys)
    ? videoCategorys
    : [];

  const totalPages = Math.max(
    1,
    Number(pagination?.totalPages) || 1
  );

  const visiblePages = getVisiblePages(
    page,
    totalPages
  );

  /* ======================================
     TRANSLATIONS
  ====================================== */

  const content = {
    eyebrow: isEnglish
      ? "AL NOMAN LEGAL MEDIA"
      : "المكتبة المرئية لمؤسسة النعمان",

    title: isEnglish
      ? "Legal Knowledge in Motion"
      : "معرفة قانونية بالصوت والصورة",

    description: isEnglish
      ? "Explore educational videos covering legal concepts, practical questions, and important aspects of Yemeni law."
      : "مجموعة من الفيديوهات القانونية التي تتناول المفاهيم الأساسية والمسائل العملية والأسئلة المهمة في القانون اليمني.",

    explore: isEnglish
      ? "Explore Video Topics"
      : "استكشف موضوعات الفيديو",

    imageLabel: isEnglish
      ? "Legal Video Library"
      : "مكتبة الفيديوهات القانونية",

    imageDescription: isEnglish
      ? "Watch. Understand. Discover."
      : "شاهد، افهم، واكتشف",

    sectionEyebrow: isEnglish
      ? "WATCH BY SUBJECT"
      : "شاهد حسب الموضوع",

    sectionTitle: isEnglish
      ? "Explore Our Video Categories"
      : "اكتشف تصنيفات الفيديوهات",

    sectionDescription: isEnglish
      ? "Choose a topic and explore a collection of legal videos addressing relevant questions and concepts."
      : "اختر المجال الذي يهمك للوصول إلى فيديوهات تتناول الأسئلة والمفاهيم القانونية المرتبطة به.",

    sectionLabel: isEnglish
      ? "Organized Legal Content"
      : "محتوى قانوني منظم",

    emptyTitle: isEnglish
      ? "No Video Categories Available"
      : "لا توجد تصنيفات فيديو متاحة",

    emptyText: isEnglish
      ? "Video categories will appear here when they become available."
      : "ستظهر تصنيفات الفيديوهات هنا عند إضافتها.",

    previous: isEnglish ? "Previous" : "السابق",
    next: isEnglish ? "Next" : "التالي",
    page: isEnglish ? "Page" : "صفحة",
    of: isEnglish ? "of" : "من",
  };

  /* ======================================
     PAGINATION ACTION
  ====================================== */

  const handlePageChange = (nextPage) => {
    if (
      nextPage < 0 ||
      nextPage >= totalPages ||
      nextPage === page ||
      isLoading
    ) {
      return;
    }

    setPage(nextPage);

    document
      .getElementById("video-categories")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  const scrollToCategories = () => {
    document
      .getElementById("video-categories")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  return (
    <>
      <Header active={5} />

      <main className="min-h-screen overflow-hidden bg-[#F0F5F8] dark:bg-[#0D2030]">

        {/* ======================================
            01 — VIDEO HERO
        ====================================== */}

        <section
          className="
            relative isolate overflow-hidden
            bg-[#102739]
            text-white
            dark:bg-[#081927]
          "
        >
          {/* Soft decorative gradients */}
          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute -end-40 -top-48
              h-[550px] w-[550px]
              rounded-full
              bg-[#5785A0]/10
              blur-[110px]
            "
          />

          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute -start-40 -bottom-48
              h-[400px] w-[400px]
              rounded-full
              bg-[#C69C59]/[0.04]
              blur-[100px]
            "
          />

          <div className="container relative z-10">
            <div
              className="
                grid items-center
                gap-12 py-14

                md:py-16

                lg:min-h-[550px]
                lg:grid-cols-2
                lg:gap-16
                lg:py-20

                xl:gap-24
              "
            >
              {/* ==================================
                  HERO TEXT
              ================================== */}

              <div className="min-w-0">

                {/* Eyebrow */}
                <div className="mb-7 flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="h-px w-10 bg-[#D7B77F]"
                  />

                  <span className="eyebrow text-[#D7B77F]">
                    {content.eyebrow}
                  </span>
                </div>

                {/* Main heading */}
                <h1 className="h1 max-w-[740px] text-white">
                  {content.title}
                </h1>

                <div
                  aria-hidden="true"
                  className="
                    mt-7 h-[2px] w-16
                    rounded-full bg-[#D7B77F]
                  "
                />

                {/* Description */}
                <p
                  className="
                    body-1 mt-7
                    max-w-[620px]
                    text-[#C3D2DD]
                  "
                >
                  {content.description}
                </p>

                {/* Explore button */}
                <button
                  type="button"
                  onClick={scrollToCategories}
                  className="
                    group mt-10 inline-flex
                    min-h-[54px]
                    items-center justify-center
                    gap-4

                    rounded-xl
                    border border-white/20
                    bg-white/[0.09]
                    px-6 py-3

                    text-white
                    backdrop-blur-xl

                    transition-all duration-300

                    hover:border-white/40
                    hover:bg-white/[0.16]

                    focus-visible:outline-none
                    focus-visible:ring-2
                    focus-visible:ring-[#D7B77F]
                  "
                >
                  <span className="button-sm">
                    {content.explore}
                  </span>

                  <span
                    className="
                      flex h-8 w-8
                      items-center justify-center
                      rounded-lg
                      bg-white/10

                      transition-transform duration-300
                      group-hover:translate-y-1
                    "
                  >
                    <HiOutlineArrowDown
                      size={19}
                      aria-hidden="true"
                    />
                  </span>
                </button>

                {/* Supporting information */}
                <div
                  className="
                    mt-11 flex items-center gap-3
                    border-t border-white/10
                    pt-6
                  "
                >
                  <HiOutlineVideoCamera
                    size={24}
                    aria-hidden="true"
                    className="shrink-0 text-[#D7B77F]"
                  />

                  <span className="body-3 text-white/70">
                    {content.imageLabel}
                  </span>
                </div>
              </div>

              {/* ==================================
                  VIDEO PREVIEW IMAGE
              ================================== */}

              <div className="relative min-w-0">

                {/* Decorative corner */}
                <div
                  aria-hidden="true"
                  className="
                    pointer-events-none
                    absolute -end-4 -top-4
                    h-32 w-32
                    border-e border-t
                    border-[#D7B77F]/60
                  "
                />

                {/* Preview frame */}
                <div
                  className="
                    group relative overflow-hidden
                    rounded-[22px]

                    border border-white/15
                    bg-[#18364A]

                    shadow-[0_30px_70px_rgba(0,0,0,0.23)]
                  "
                >
                  {/* Main image */}
                  <img
                    src={VideoCategoryImage}
                    alt={content.imageLabel}
                    fetchPriority="high"
                    decoding="async"
                    className="
                      aspect-[16/11]
                      w-full object-cover

                      transition-transform duration-700
                      group-hover:scale-[1.04]

                      lg:aspect-[5/4]
                    "
                  />

                  {/* Image overlay */}
                  <div
                    aria-hidden="true"
                    className="
                      pointer-events-none
                      absolute inset-0

                      bg-gradient-to-t
                      from-[#061522]/85
                      via-[#061522]/15
                      to-[#061522]/15
                    "
                  />

                  {/* Decorative video frame */}
                  <div
                    aria-hidden="true"
                    className="
                      absolute inset-5
                      rounded-xl
                      border border-white/15
                      sm:inset-7
                    "
                  />

                  {/* Central play emblem */}
                  <div
                    aria-hidden="true"
                    className="
                      absolute left-1/2 top-1/2
                      flex h-[82px] w-[82px]
                      -translate-x-1/2
                      -translate-y-1/2
                      items-center justify-center

                      rounded-full
                      border border-white/35
                      bg-white/15

                      text-white
                      backdrop-blur-lg

                      shadow-[0_12px_35px_rgba(0,0,0,0.25)]

                      sm:h-[94px]
                      sm:w-[94px]
                    "
                  >
                    <HiOutlinePlay
                      size={37}
                      className="ms-1"
                    />
                  </div>

                  {/* Top label */}
                  <div
                    className="
                      absolute start-9 top-9
                      flex items-center gap-2

                      rounded-lg
                      border border-white/20
                      bg-[#102031]/55

                      px-3 py-2
                      backdrop-blur-lg
                    "
                  >
                    <HiOutlineVideoCamera
                      size={17}
                      className="text-[#D7B77F]"
                    />

                    <span className="font-cairo text-xs font-semibold text-white">
                      AL NOMAN
                    </span>
                  </div>

                  {/* Bottom description */}
                  <div className="absolute inset-x-9 bottom-9">

                    <span className="mb-2 block h-[2px] w-9 bg-[#D7B77F]" />

                    <p className="font-cairo text-base font-semibold text-white sm:text-lg">
                      {content.imageDescription}
                    </p>
                  </div>
                </div>

                {/* Below image detail */}
                <div
                  className="
                    mt-5 flex items-center gap-3
                    text-white/60
                  "
                >
                  <span className="h-px w-9 bg-[#D7B77F]/70" />

                  <span className="font-cairo text-xs font-medium">
                    {content.imageLabel}
                  </span>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ======================================
            02 — VIDEO CATEGORIES
        ====================================== */}

        <section
          id="video-categories"
          aria-labelledby="video-categories-heading"
          className="
            relative scroll-mt-[90px]
            overflow-hidden

            border-t border-[#DCE6ED]
            bg-[#F0F5F8]

            py-16
            sm:py-20
            lg:py-24

            dark:border-white/10
            dark:bg-[#112A3C]
          "
        >
          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute -end-32 -top-40
              h-[400px] w-[400px]
              rounded-full
              bg-white/35
              blur-3xl
              dark:bg-white/[0.025]
            "
          />

          <div className="container relative">

            {/* ==================================
                SECTION HEADING
            ================================== */}

            <div
              className="
                mb-12 flex flex-col
                justify-between gap-7

                lg:flex-row
                lg:items-end
                lg:gap-12
              "
            >
              <div className="max-w-[780px]">

                {/* Eyebrow */}
                <div className="mb-5 flex items-center gap-3">
                  <span className="h-px w-9 bg-[#C19B60]" />

                  <span className="eyebrow text-[#AA8044] dark:text-[#D7B77F]">
                    {content.sectionEyebrow}
                  </span>
                </div>

                {/* Heading */}
                <h2
                  id="video-categories-heading"
                  className="
                    h2 text-[#19344D]
                    dark:text-white
                  "
                >
                  {content.sectionTitle}
                </h2>

                {/* Supporting description */}
                <p
                  className="
                    body-2 mt-5 max-w-[650px]
                    text-[#62778A]
                    dark:text-[#B5C8D6]
                  "
                >
                  {content.sectionDescription}
                </p>
              </div>

              {/* Small section identity */}
              <div
                className="
                  hidden shrink-0
                  items-center gap-3
                  lg:flex
                "
              >
                <span className="font-cairo text-sm text-[#62778A] dark:text-[#B5C8D6]">
                  {content.sectionLabel}
                </span>

                <div
                  className="
                    flex h-12 w-12
                    items-center justify-center

                    rounded-xl
                    border border-[#D9E4EC]
                    bg-white

                    text-[#C19B60]

                    dark:border-white/10
                    dark:bg-[#1D394D]
                    dark:text-[#D7B77F]
                  "
                >
                  <HiOutlineRectangleStack
                    size={24}
                    aria-hidden="true"
                  />
                </div>
              </div>
            </div>

            {/* ==================================
                CONTENT
            ================================== */}

            {isLoading ? (
              <div className="flex min-h-[350px] items-center justify-center">
                <Loader />
              </div>
            ) : categories.length > 0 ? (
              <>
                {/* Categories grid */}
                <div
                  className="
                    grid grid-cols-1
                    items-stretch gap-6

                    md:grid-cols-2
                    lg:grid-cols-3
                    lg:gap-7
                  "
                >
                  {categories.map((category, index) => (
                    <VideoCategoryCard
                      key={category._id || index}
                      category={category}
                      index={page * PAGE_SIZE + index}
                    />
                  ))}
                </div>

                {/* ==================================
                    PAGINATION
                ================================== */}

                {totalPages > 1 && (
                  <nav
                    aria-label={
                      isEnglish
                        ? "Video categories pagination"
                        : "ترقيم صفحات تصنيفات الفيديو"
                    }
                    className="
                      mt-14 flex flex-col
                      items-center justify-between
                      gap-6

                      border-t border-[#D9E4EC]
                      pt-8

                      sm:flex-row

                      dark:border-white/10
                    "
                  >
                    {/* Page counter */}
                    <div className="flex items-center gap-2 font-cairo text-sm tabular-nums">
                      <span className="text-[#62778A] dark:text-[#B5C8D6]">
                        {content.page}
                      </span>

                      <span className="font-bold text-[#19344D] dark:text-white">
                        {String(page + 1).padStart(2, "0")}
                      </span>

                      <span className="text-[#62778A] dark:text-[#B5C8D6]">
                        {content.of}
                      </span>

                      <span className="text-[#62778A] dark:text-[#B5C8D6]">
                        {String(totalPages).padStart(2, "0")}
                      </span>
                    </div>

                    {/* Pagination buttons */}
                    <div className="flex flex-wrap items-center justify-center gap-2">

                      {/* Previous */}
                      <button
                        type="button"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 0 || isLoading}
                        aria-label={content.previous}
                        className="
                          flex h-11 w-11
                          items-center justify-center
                          rounded-xl

                          border border-[#D9E4EC]
                          bg-white
                          text-[#19344D]

                          transition-colors duration-200
                          hover:border-[#C19B60]/50

                          disabled:cursor-not-allowed
                          disabled:opacity-40

                          dark:border-white/10
                          dark:bg-[#1B3549]
                          dark:text-white

                          focus-visible:outline-none
                          focus-visible:ring-2
                          focus-visible:ring-[#C19B60]
                        "
                      >
                        <HiOutlineChevronLeft
                          size={20}
                          className="directional-arrow"
                        />
                      </button>

                      {/* Page numbers */}
                      {visiblePages.map((number) => {
                        const active = number === page;

                        return (
                          <button
                            type="button"
                            key={number}
                            onClick={() => handlePageChange(number)}
                            aria-label={`${content.page} ${number + 1}`}
                            aria-current={active ? "page" : undefined}
                            disabled={isLoading}
                            className={`
                              flex h-11 min-w-[44px]
                              items-center justify-center

                              rounded-xl border px-3
                              font-cairo text-sm font-semibold
                              tabular-nums

                              transition-all duration-200

                              focus-visible:outline-none
                              focus-visible:ring-2
                              focus-visible:ring-[#C19B60]

                              ${
                                active
                                  ? "border-[#19344D] bg-[#19344D] text-white dark:border-white/20 dark:bg-white/15"
                                  : "border-[#D9E4EC] bg-white text-[#62778A] hover:border-[#C19B60]/50 dark:border-white/10 dark:bg-[#1B3549] dark:text-[#C5D5E0]"
                              }
                            `}
                          >
                            {String(number + 1).padStart(2, "0")}
                          </button>
                        );
                      })}

                      {/* Next */}
                      <button
                        type="button"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={
                          page >= totalPages - 1 || isLoading
                        }
                        aria-label={content.next}
                        className="
                          flex h-11 w-11
                          items-center justify-center
                          rounded-xl

                          border border-[#D9E4EC]
                          bg-white
                          text-[#19344D]

                          transition-colors duration-200
                          hover:border-[#C19B60]/50

                          disabled:cursor-not-allowed
                          disabled:opacity-40

                          dark:border-white/10
                          dark:bg-[#1B3549]
                          dark:text-white

                          focus-visible:outline-none
                          focus-visible:ring-2
                          focus-visible:ring-[#C19B60]
                        "
                      >
                        <HiOutlineChevronRight
                          size={20}
                          className="directional-arrow"
                        />
                      </button>
                    </div>
                  </nav>
                )}
              </>
            ) : (
              /* ==================================
                  EMPTY STATE
              ================================== */

              <div
                className="
                  flex min-h-[330px]
                  flex-col items-center
                  justify-center gap-5

                  rounded-2xl
                  border border-[#D9E4EC]
                  bg-white
                  px-6 py-12
                  text-center

                  dark:border-white/10
                  dark:bg-[#1B3549]
                "
              >
                <div
                  className="
                    flex h-16 w-16
                    items-center justify-center
                    rounded-2xl

                    bg-[#F0F5F8]
                    text-[#C19B60]

                    dark:bg-white/5
                    dark:text-[#D7B77F]
                  "
                >
                  <HiOutlineVideoCamera size={31} />
                </div>

                <h3 className="h5 text-[#19344D] dark:text-white">
                  {content.emptyTitle}
                </h3>

                <p className="body-2 max-w-[470px] text-[#62778A] dark:text-[#B5C8D6]">
                  {content.emptyText}
                </p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Fotter />
    </>
  );
}

export default VideoCategoryPage;
